import { ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './NachrichtenView.module.scss';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import { getData, markAllMessagesAsRead } from '../../shared/fetchApi';
import Config from '../../shared/config';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';

export type Nachricht = {
    id?: string;
    titel?: string;
    title?: string;
    text?: string;
    inhalt?: string;
    content?: string;
    message?: string;
    datum?: string;
    date?: string;
    createdAt?: string;
    erstelltAm?: string;
    gelesen?: boolean;
    read?: boolean;
    link?: string;
};

function getNachrichtTitle(nachricht: Nachricht): string {
    return nachricht.titel ?? nachricht.title ?? 'Nachricht';
}

function getNachrichtText(nachricht: Nachricht): string {
    return nachricht.text ?? nachricht.inhalt ?? nachricht.content ?? nachricht.message ?? '';
}

function getNachrichtDate(nachricht: Nachricht): string {
    const dateStr = nachricht.datum ?? nachricht.date ?? nachricht.createdAt ?? nachricht.erstelltAm ?? '';
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

function sortNachrichtenNeuesteZuerst(list: Nachricht[]): Nachricht[] {
    return [...list].sort((a, b) => {
        const dateStrA = a.datum ?? a.date ?? a.createdAt ?? a.erstelltAm ?? '';
        const dateStrB = b.datum ?? b.date ?? b.createdAt ?? b.erstelltAm ?? '';
        const timeA = dateStrA ? new Date(dateStrA).getTime() : 0;
        const timeB = dateStrB ? new Date(dateStrB).getTime() : 0;
        return timeB - timeA;
    });
}

function isExternalLink(link: string): boolean {
    return /^https?:\/\//i.test(link);
}

const NachrichtenView = (): ReactElement => {
    const pageTitle = 'Nachrichten';
    useDocumentTitle(pageTitle);

    const [nachrichten, setNachrichten] = useState<Nachricht[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        const endpoint = Config.getConfig().nachrichtenEndpoint;
        const fetchNachrichten = async () => {
            try {
                const data = await getData<Nachricht[] | { content?: Nachricht[] } | Nachricht>(endpoint);
                if (Array.isArray(data)) {
                    setNachrichten(sortNachrichtenNeuesteZuerst(data));
                } else if (
                    data &&
                    typeof data === 'object' &&
                    'content' in data &&
                    Array.isArray((data as { content?: Nachricht[] }).content)
                ) {
                    setNachrichten(sortNachrichtenNeuesteZuerst((data as { content: Nachricht[] }).content));
                } else if (data && typeof data === 'object') {
                    setNachrichten([data as Nachricht]);
                } else {
                    setNachrichten([]);
                }
            } catch (error) {
                console.error('Fehler beim Laden der Nachrichten:', error);
                setLoadError('Die Nachrichten konnten nicht geladen werden.');
                setNachrichten([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNachrichten();
        markAllMessagesAsRead();
    }, []);

    return (
        <div className={styles._container}>
            <LoadingSpinner isLoading={isLoading}>
                <div className={styles._content}>
                    <h1 className={styles._pageTitle}>{pageTitle}</h1>

                    {loadError && <div className={styles._error}>{loadError}</div>}

                    {!loadError && nachrichten.length === 0 && (
                        <div className={styles._emptyState}>
                            <p>Keine Nachrichten vorhanden.</p>
                        </div>
                    )}

                    {!loadError && nachrichten.length > 0 && (
                        <ul className={styles._list}>
                            {nachrichten.map((nachricht, index) => {
                                const link = nachricht.link;
                                const content = (
                                    <>
                                        <h2 className={styles._itemTitle}>{getNachrichtTitle(nachricht)}</h2>
                                        {getNachrichtDate(nachricht) && <p className={styles._itemDate}>{getNachrichtDate(nachricht)}</p>}
                                        {getNachrichtText(nachricht) && <p className={styles._itemText}>{getNachrichtText(nachricht)}</p>}
                                    </>
                                );
                                return (
                                    <li key={nachricht.id ?? index} className={styles._listItem}>
                                        {link ? (
                                            isExternalLink(link) ? (
                                                <a href={link} className={styles._listItemLink} target="_blank" rel="noopener noreferrer">
                                                    {content}
                                                </a>
                                            ) : (
                                                <Link to={link} className={styles._listItemLink}>
                                                    {content}
                                                </Link>
                                            )
                                        ) : (
                                            <span className={styles._listItemLink}>{content}</span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </LoadingSpinner>
        </div>
    );
};

export default NachrichtenView;
